import React from "react";
import { Info } from "@phosphor-icons/react";

/**
 * Iter 19.66.3 — Cap-Bypass-Sonderregel-Hinweise (Phase 2 von Iter 19.66).
 *
 * Rein informative Banner unter dem „Selber Arbeitgeber"-Hakerl im Schritt 3.
 * Werden eingeblendet, sobald das KV mindestens eine aktive Sonderregel
 * in `cap_bypass_sonderregeln` hat UND die Checkbox auf der Zeile sichtbar
 * ist (= globale Selber-AG-Regel greift + Höchstanrechnungsdauer erreicht).
 *
 * Math-Engine konsumiert dieses Feld (noch) NICHT — Hinweise dienen
 * ausschließlich der Sachbearbeiter-Aufklärung. Backlog: Phase 3
 * (Backend-Auswertung der drei Sonderregel-Typen inkl. Pytest).
 *
 * Texte sind bewusst HR-freundlich gehalten: „Höchstanrechnungsdauer"
 * statt „Cap", „Sonderregel" statt „Bypass".
 */

const RULE_TEMPLATES = {
  selber_arbeitgeber_lueckenregel: (rule) => {
    const monate = Number(rule.max_lueckendauer_monate) || 0;
    if (monate <= 0) return null;
    return (
      <>
        Diese Sonderregel gilt nur, wenn die zeitliche Lücke zwischen dieser
        Vordienstzeit und der aktuellen Anstellung höchstens{" "}
        <strong className="font-semibold">{monate} Monate</strong> beträgt.
        Bitte selbst prüfen.
      </>
    );
  },
  selber_arbeitgeber_vorarbeiterzeit: (rule) => {
    const faktor = Number(rule.faktor);
    const maxJahre = Number(rule.max_jahre) || 0;
    const prozent =
      Number.isFinite(faktor) && faktor > 0
        ? Math.round(faktor * 100)
        : null;
    return (
      <>
        Vorarbeiterzeiten beim selben Arbeitgeber werden{" "}
        {prozent !== null && (
          <>
            mit <strong className="font-semibold">{prozent}%</strong>{" "}
          </>
        )}
        angerechnet
        {maxJahre > 0 && (
          <>
            , max.{" "}
            <strong className="font-semibold">{maxJahre} Jahre</strong> in
            Summe
          </>
        )}
        . Bitte selbst berücksichtigen.
      </>
    );
  },
  selber_arbeitgeber_gleiche_gruppe: () => (
    <>
      Diese Sonderregel gilt nur, wenn die Vordienst-Position der{" "}
      <strong className="font-semibold">gleichen oder einer höheren
      Verwendungsgruppe</strong> entsprach. Bitte selbst eruieren.
    </>
  ),
  // Iter 20.36.f2 — WT-Kanzlei-Kombiregel: HR-freundlicher Hinweis pro
  // Zeile. Iter 20.36.f5 — neue Feldnamen und Zwei-Töpfe-Semantik.
  wt_kanzlei_kombiregel: (rule) => {
    const maxJahreWT = Number(rule.max_jahre_wt) || 0;
    const maxJahreNonWT = Number(rule.max_jahre_non_wt) || 0;
    const faktorNonWT = Number(rule.faktor_non_wt);
    const prozent =
      Number.isFinite(faktorNonWT) && faktorNonWT > 0
        ? Math.round(faktorNonWT * 100)
        : null;
    return (
      <>
        Ist die Vordienstzeit in einer{" "}
        <strong className="font-semibold">Wirtschaftstreuhand-Kanzlei</strong>{" "}
        erbracht worden, zählt sie voll
        {maxJahreWT > 0 && (
          <>
            {" "}
            (Gesamt-Höchstanrechnungsdauer{" "}
            <strong className="font-semibold">{maxJahreWT} Jahre</strong>)
          </>
        )}
        . Andernfalls zählt sie
        {prozent !== null && (
          <>
            {" "}
            nur mit{" "}
            <strong className="font-semibold">{prozent} %</strong>
          </>
        )}
        {maxJahreNonWT > 0 && (
          <>
            {" "}
            und max. bis zu{" "}
            <strong className="font-semibold">{maxJahreNonWT} Jahre</strong>{" "}
            effektiv
          </>
        )}
        . Bitte pro Zeile über die Checkbox &bdquo;In WT-Kanzlei erbracht&ldquo;
        markieren.
      </>
    );
  },
};

export default function CapBypassHints({ sonderregeln, rowIndex }) {
  if (!Array.isArray(sonderregeln) || sonderregeln.length === 0) return null;
  const active = sonderregeln.filter((r) => r && r.aktiv !== false);
  if (active.length === 0) return null;

  return (
    <div
      className="px-3 pb-2 -mt-1 space-y-1.5"
      data-testid={`vd-row-${rowIndex}-cap-bypass-hints`}
    >
      {active.map((rule, i) => {
        const template = RULE_TEMPLATES[rule.typ];
        if (!template) return null;
        const content = template(rule);
        if (!content) return null;
        return (
          <div
            key={`${rule.typ}-${i}`}
            className="flex items-start gap-1.5 rounded-sm border border-sky-200 bg-sky-50/60 px-2 py-1.5 text-[11px] leading-snug text-sky-900"
            data-testid={`vd-row-${rowIndex}-cap-bypass-hint-${rule.typ}`}
            role="note"
          >
            <Info
              size={13}
              weight="fill"
              className="mt-0.5 shrink-0 text-sky-600"
            />
            <span>{content}</span>
          </div>
        );
      })}
    </div>
  );
}
